import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowUpRight } from "lucide-react";
import { rhm, money, dt, titleCase } from "../api";
import { Card, Empty, PageHead, StatCard, Table, Td, Badge, statusTone } from "../ui";

export default function RhmDashboard() {
    const { data, isLoading } = useQuery({
        queryKey: ["rhm-dashboard"],
        queryFn: async () => (await rhm.get("/dashboard")).data,
        refetchInterval: 60000,
    });

    if (isLoading) return <div className="mono text-xs text-zinc-500">LOADING DASHBOARD…</div>;
    const c = data?.cards || {};
    const actions = data?.action_required || [];

    return (
        <div data-testid="rhm-dashboard">
            <PageHead title="Dashboard" sub="Live operating picture across commerce, inventory and CRM." />

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                <StatCard testId="stat-orders" label="Total Orders" value={c.total_orders ?? 0} />
                <StatCard testId="stat-sales" label="Sales Value" value={money(c.sales_value)} tone="gold" />
                <StatCard testId="stat-customers" label="Customers" value={c.customers ?? 0} />
                <StatCard testId="stat-dealers" label="Dealers" value={c.dealers ?? 0} />
                <StatCard testId="stat-products" label="Products" value={c.products ?? 0} />
                <StatCard testId="stat-inventory" label="Inventory Units" value={c.inventory_units ?? 0} tone="good" />
                <StatCard testId="stat-low-stock" label="Low Stock" value={c.low_stock ?? 0} tone={c.low_stock ? "warn" : "default"} />
                <StatCard testId="stat-out-of-stock" label="Out of Stock" value={c.out_of_stock ?? 0} tone={c.out_of_stock ? "danger" : "default"} />
                <StatCard testId="stat-expiring" label="Expiring Soon" value={c.expiring_soon ?? 0} tone={c.expiring_soon ? "warn" : "default"} />
                <StatCard testId="stat-pending-orders" label="Pending Orders" value={c.pending_orders ?? 0} />
                <StatCard testId="stat-reorders" label="Reorders Due" value={c.reorders_due ?? 0} />
                <StatCard testId="stat-notify" label="Notify-Me Requests" value={c.notify_requests ?? 0} />
            </div>

            <div className="grid lg:grid-cols-2 gap-5 mt-6">
                <Card title="Action Required" data-testid="rhm-action-required">
                    {actions.length === 0 ? (
                        <Empty msg="Nothing needs attention right now." />
                    ) : (
                        <ul className="space-y-3">
                            {actions.map((a, i) => (
                                <li key={i} className="flex items-start gap-3 border border-white/8 p-3">
                                    <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${a.severity === "high" ? "text-red-400" : "text-gold"}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold">{a.label}</div>
                                        <div className="text-xs text-zinc-500 mt-0.5">{a.detail}</div>
                                    </div>
                                    {a.link && (
                                        <Link to={a.link} className="mono text-[9px] tracking-[0.16em] text-gold inline-flex items-center gap-1">
                                            OPEN <ArrowUpRight className="w-3 h-3" />
                                        </Link>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                <Card title="Recent Orders">
                    <Table head={["Order", "Customer", "Total", "Status", "Placed"]}
                        empty={!data?.recent_orders?.length && <Empty msg="No orders yet." />}>
                        {(data?.recent_orders || []).map((o) => (
                            <tr key={o.order_number}>
                                <Td className="mono text-xs text-gold">{o.order_number}</Td>
                                <Td>{o.address?.full_name}</Td>
                                <Td>{money(o.total)}</Td>
                                <Td><Badge tone={statusTone(o.status)}>{titleCase(o.status)}</Badge></Td>
                                <Td className="text-xs text-zinc-500">{dt(o.created_at)}</Td>
                            </tr>
                        ))}
                    </Table>
                </Card>
            </div>

            <Card title="Recent Activity" className="mt-5">
                <Table head={["When", "User", "Action", "Record"]}
                    empty={!data?.recent_activity?.length && <Empty msg="No activity logged yet." />}>
                    {(data?.recent_activity || []).map((a) => (
                        <tr key={a.id}>
                            <Td className="text-xs text-zinc-500 whitespace-nowrap">{dt(a.created_at)}</Td>
                            <Td className="text-sm">{a.user_name}</Td>
                            <Td><Badge>{titleCase(a.action)}</Badge></Td>
                            <Td className="text-sm text-zinc-400">{a.record_label || a.record_type}</Td>
                        </tr>
                    ))}
                </Table>
            </Card>
        </div>
    );
}
